import React, { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Media } from '../services/api';

interface PhotoGridProps {
  media: Media[];
  onMediaClick: (media: Media) => void;
  isVideoFilterActive: boolean;
  viewportHeight?: number;
  containerWidth?: number;
}

const PhotoItem: React.FC<{ media: Media; onClick: () => void }> = ({ media, onClick }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <div
      ref={ref}
      className="media-item group"
      onClick={onClick}
    >
      {inView ? (
        <img
          src={media.thumbnail_url || media.url}
          alt={media.filename}
          className="w-full h-full object-cover object-top"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
          </svg>
        </div>
      )}
      
      {/* Media overlay */}
      <div className="media-overlay">
        {media.file_type === 'video' ? (
          <i className="fas fa-play"></i>
        ) : (
          <i className="fas fa-image"></i>
        )}
      </div>
    </div>
  );
};

const PhotoGrid: React.FC<PhotoGridProps> = ({ media, onMediaClick, isVideoFilterActive, viewportHeight = 800, containerWidth = 1200 }) => {
  // Filter media based on video filter
  const filteredMedia = useMemo(() => {
    if (isVideoFilterActive) {
      return media.filter(item => item.file_type === 'video');
    }
    return media;
  }, [media, isVideoFilterActive]);

  if (filteredMedia.length === 0) {
    const message = isVideoFilterActive ? 'No videos in this album' : 'No media in this album';
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p>{message}</p>
        </div>
      </div>
    );
  }

  // Calculate optimal column size based on container width
  // Use more columns to fill the width better
  const optimalColumnSize = Math.max(120, Math.floor(containerWidth / 12)); // Aim for ~12 columns

  return (
    <div 
      className="media-grid"
      style={{ 
        gridTemplateColumns: `repeat(auto-fill, minmax(${optimalColumnSize}px, 1fr))`,
        width: '100%',
        maxWidth: '100%'
      }}
    >
      {filteredMedia.map((item) => (
        <PhotoItem
          key={item.id}
          media={item}
          onClick={() => onMediaClick(item)}
        />
      ))}
    </div>
  );
};

export default PhotoGrid;
